// Layouts/Notifications/NotificationSettings.tsx
//
// Agency notification settings (GET / PUT /api/v1/notification-settings).
//   • Everyone in the agency can open it; only an admin can change it.
//   • Times are IST, 24h "HH:MM" (same clock the backend uses).
//   • Saving re-plans the agency's pending follow-up notifications (backend does it).
//
// Route (add next to the other settings pages):
//   <Route path="/settings/notifications" element={<NotificationSettings />} />

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Toast } from 'primereact/toast';
import {
  BellRing,
  CalendarClock,
  AlarmClockOff,
  Zap,
  Users,
  ShieldCheck,
  Save,
  Loader2,
  RotateCcw,
  X,
  Info,
  AlertTriangle,
  Lock,
  CheckCircle2,
} from 'lucide-react';
import apiClient from '../../stateManagers/apiSetup/axiosInterceptor';

// ── Design tokens (same values as the other panel screens) ───────────────────
const C = {
  teal: '#115550',
  tealMid: '#16706a',
  lime: '#c8e94b',
  limeDim: 'rgba(200,233,75,0.18)',
  limeBorder: 'rgba(200,233,75,0.4)',
  white: '#ffffff',
  muted: '#9ca3af',
  sub: '#6b7280',
};
const G = {
  shellBg: '#f8f9f9',
  shellBorder: '#e8ebeb',
  line: '#e5e7eb',
  soft: '#eef0f0',
};
const SHELL: React.CSSProperties = {
  background: G.shellBg,
  border: `1px solid ${G.shellBorder}`,
  borderRadius: '13px',
  padding: '4.6px',
};
const INNER: React.CSSProperties = {
  background: C.white,
  border: `1px solid ${G.line}`,
  borderRadius: '9.2px',
};

// ── Types (ObjectIDs are strings) ────────────────────────────────────────────
type SlotKey = 'follow_up_staff' | 'follow_up_admin' | 'overdue_staff' | 'overdue_admin';
type FlagKey = 'notify_admin_on_qualified' | 'notify_staff_on_assigned';

interface RoleTimes {
  enabled: boolean;
  /** "HH:MM", 24h, IST */
  time: string;
}

interface Editable {
  follow_up_staff: RoleTimes;
  follow_up_admin: RoleTimes;
  overdue_staff: RoleTimes;
  overdue_admin: RoleTimes;
  notify_admin_on_qualified: boolean;
  notify_staff_on_assigned: boolean;
}

interface NotificationSettingsDoc extends Editable {
  id?: string;
  agency_id: string;
  updated_by?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
}

interface SettingsResponse {
  success: boolean;
  is_default?: boolean;
  timezone?: string;
  message?: string;
  error?: string;
  data: NotificationSettingsDoc;
}

// Same defaults as models.DefaultNotificationSettings in the backend.
const DEFAULTS: Editable = {
  follow_up_staff: { enabled: true, time: '09:00' },
  follow_up_admin: { enabled: true, time: '09:30' },
  overdue_staff: { enabled: true, time: '10:00' },
  overdue_admin: { enabled: true, time: '10:30' },
  notify_admin_on_qualified: true,
  notify_staff_on_assigned: true,
};

const EDITABLE_KEYS = Object.keys(DEFAULTS) as (keyof Editable)[];
const CLOCK_RE = /^([01]\d|2[0-3]):[0-5]\d$/;

const pickEditable = (d: NotificationSettingsDoc): Editable => ({
  follow_up_staff: { enabled: !!d.follow_up_staff?.enabled, time: d.follow_up_staff?.time || DEFAULTS.follow_up_staff.time },
  follow_up_admin: { enabled: !!d.follow_up_admin?.enabled, time: d.follow_up_admin?.time || DEFAULTS.follow_up_admin.time },
  overdue_staff: { enabled: !!d.overdue_staff?.enabled, time: d.overdue_staff?.time || DEFAULTS.overdue_staff.time },
  overdue_admin: { enabled: !!d.overdue_admin?.enabled, time: d.overdue_admin?.time || DEFAULTS.overdue_admin.time },
  notify_admin_on_qualified: !!d.notify_admin_on_qualified,
  notify_staff_on_assigned: !!d.notify_staff_on_assigned,
});

const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);

/** "14:05" → "2:05 PM" */
const to12h = (hhmm: string) => {
  const [h, m] = hhmm.split(':').map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return hhmm;
  return `${h % 12 === 0 ? 12 : h % 12}:${String(m).padStart(2, '0')} ${h >= 12 ? 'PM' : 'AM'}`;
};

const formatSaved = (iso?: string | null) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime()) || d.getFullYear() < 2000) return null; // Go zero time
  return d.toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit' });
};

// ── Small UI pieces ──────────────────────────────────────────────────────────
const Toggle: React.FC<{ on: boolean; onChange: (v: boolean) => void; disabled?: boolean; label: string }> = ({
  on,
  onChange,
  disabled,
  label,
}) => (
  <button
    type="button"
    role="switch"
    aria-checked={on}
    aria-label={label}
    disabled={disabled}
    onClick={() => onChange(!on)}
    style={{
      position: 'relative',
      width: '38px',
      height: '22px',
      flexShrink: 0,
      borderRadius: '999px',
      border: 'none',
      padding: 0,
      background: on ? C.teal : '#d1d5db',
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.55 : 1,
      transition: 'background 0.15s',
    }}
  >
    <span
      style={{
        position: 'absolute',
        top: '3px',
        left: '3px',
        width: '16px',
        height: '16px',
        borderRadius: '50%',
        background: on ? C.lime : C.white,
        boxShadow: '0 1px 2px rgba(0,0,0,0.25)',
        transform: on ? 'translateX(16px)' : 'translateX(0)',
        transition: 'transform 0.15s',
      }}
    />
  </button>
);

const Card: React.FC<{
  icon: React.ReactNode;
  title: string;
  hint: string;
  children: React.ReactNode;
}> = ({ icon, title, hint, children }) => (
  <div style={SHELL}>
    <div style={{ ...INNER, overflow: 'hidden' }}>
      <div style={{ padding: '12px 16px', borderBottom: `1px solid ${G.line}`, display: 'flex', alignItems: 'center', gap: '10px' }}>
        <div
          style={{
            width: '30px',
            height: '30px',
            borderRadius: '8px',
            background: C.teal,
            color: C.lime,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          {icon}
        </div>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: '0.82rem', fontWeight: 700, color: '#111827' }}>{title}</div>
          <div style={{ fontSize: '0.68rem', color: C.sub, marginTop: '1px' }}>{hint}</div>
        </div>
      </div>
      <div style={{ padding: '4px 0' }}>{children}</div>
    </div>
  </div>
);

const Row: React.FC<{
  icon: React.ReactNode;
  title: string;
  sub: string;
  note: string;
  children: React.ReactNode;
  dim?: boolean;
}> = ({ icon, title, sub, note, children, dim }) => (
  <div
    style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
      gap: '12px 18px',
      alignItems: 'center',
      padding: '12px 16px',
      borderBottom: `1px solid ${G.soft}`,
    }}
  >
    {/* left: what it is + the controls */}
    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', minWidth: 0 }}>
      <div style={{ color: dim ? C.muted : C.tealMid, display: 'flex' }}>{icon}</div>
      <div style={{ flex: '1 1 130px', minWidth: 0 }}>
        <div style={{ fontSize: '0.78rem', fontWeight: 600, color: dim ? C.muted : '#111827' }}>{title}</div>
        <div style={{ fontSize: '0.66rem', color: C.sub, marginTop: '1px' }}>{sub}</div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>{children}</div>
    </div>

    {/* right (opposite side): the note for this setting */}
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: '7px',
        padding: '8px 10px',
        borderRadius: '8px',
        background: dim ? G.shellBg : C.limeDim,
        border: `1px solid ${dim ? G.line : C.limeBorder}`,
        color: dim ? C.sub : C.teal,
        fontSize: '0.68rem',
        lineHeight: 1.5,
      }}
    >
      <Info size={12} style={{ flexShrink: 0, marginTop: '2px' }} />
      <span>{note}</span>
    </div>
  </div>
);

// ── Component ────────────────────────────────────────────────────────────────
const NotificationSettings: React.FC = () => {
  const toast = useRef<Toast>(null);

  const [saved, setSaved] = useState<Editable | null>(null);
  const [draft, setDraft] = useState<Editable | null>(null);
  const [isDefault, setIsDefault] = useState(false);
  const [updatedAt, setUpdatedAt] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null); // null = unknown (server decides)

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [s, p] = await Promise.all([
        apiClient.get<SettingsResponse>('/v1/notification-settings'),
        apiClient.get('/v1/profile').catch(() => null), // only needed to know the role
      ]);
      if (!s.data.success) throw new Error(s.data.error || 'Failed to load notification settings');
      const e = pickEditable(s.data.data);
      setSaved(e);
      setDraft(e);
      setIsDefault(!!s.data.is_default);
      setUpdatedAt(s.data.data.updated_at ?? null);
      const role: string | null | undefined = p?.data?.data?.role;
      setIsAdmin(role ? role === 'admin' : null);
    } catch (e: any) {
      setError(e.response?.data?.error || e.message || 'Failed to load notification settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const canEdit = isAdmin !== false;
  const dirty = useMemo(() => !!draft && !!saved && !same(draft, saved), [draft, saved]);

  // ── Edit helpers ───────────────────────────────────────────────────────────
  const setSlot = (key: SlotKey, patch: Partial<RoleTimes>) =>
    setDraft((d) => (d ? { ...d, [key]: { ...d[key], ...patch } } : d));

  const setFlag = (key: FlagKey, value: boolean) => setDraft((d) => (d ? { ...d, [key]: value } : d));

  const onTime = (key: SlotKey, value: string) => {
    // the clear button on <input type="time"> gives "" — keep the last valid time
    if (CLOCK_RE.test(value)) setSlot(key, { time: value });
  };

  const discard = () => setDraft(saved);
  const resetDefaults = () => setDraft(DEFAULTS);

  // ── Save (sends only what changed) ─────────────────────────────────────────
  const save = async () => {
    if (!draft || !saved || !dirty || saving) return;

    const body: Partial<Editable> = {};
    for (const k of EDITABLE_KEYS) {
      if (!same(draft[k], saved[k])) (body as Record<string, unknown>)[k] = draft[k];
    }

    setSaving(true);
    try {
      const res = await apiClient.put<SettingsResponse>('/v1/notification-settings', body);
      if (!res.data.success) throw new Error(res.data.error || 'Could not save settings');
      const e = pickEditable(res.data.data);
      setSaved(e);
      setDraft(e);
      setIsDefault(false);
      setUpdatedAt(res.data.data.updated_at ?? new Date().toISOString());
      toast.current?.show({
        severity: 'success',
        summary: 'Settings saved',
        detail: 'Pending follow-up notifications are being re-planned with the new times.',
        life: 4000,
      });
    } catch (e: any) {
      const forbidden = e.response?.status === 403;
      toast.current?.show({
        severity: 'error',
        summary: forbidden ? 'Admins only' : 'Could not save',
        detail: forbidden
          ? 'Only an admin can change notification settings.'
          : e.response?.data?.error || e.message || 'Something went wrong. Please try again.',
        life: 5000,
      });
    } finally {
      setSaving(false);
    }
  };

  // ── Preview of what the current draft will do ──────────────────────────────
  const preview = useMemo(() => {
    if (!draft) return [];
    const rows: { day: 0 | 1; time: string; text: string; who: 'staff' | 'admin'; kind: 'due' | 'overdue' }[] = [];
    if (draft.follow_up_staff.enabled) rows.push({ day: 0, time: draft.follow_up_staff.time, who: 'staff', kind: 'due', text: 'Assigned staff is reminded to follow up' });
    if (draft.follow_up_admin.enabled) rows.push({ day: 0, time: draft.follow_up_admin.time, who: 'admin', kind: 'due', text: 'Admins are told a follow-up is due' });
    if (draft.overdue_staff.enabled) rows.push({ day: 1, time: draft.overdue_staff.time, who: 'staff', kind: 'overdue', text: 'Assigned staff is told the follow-up was missed' });
    if (draft.overdue_admin.enabled) rows.push({ day: 1, time: draft.overdue_admin.time, who: 'admin', kind: 'overdue', text: 'Admins are told the follow-up was missed' });
    return rows.sort((a, b) => a.day - b.day || a.time.localeCompare(b.time));
  }, [draft]);

  // ── Shared styles ──────────────────────────────────────────────────────────
  const timeInput = (enabled: boolean): React.CSSProperties => ({
    height: '31px',
    padding: '0 9px',
    border: `1px solid ${G.line}`,
    borderRadius: '7.4px',
    background: enabled && canEdit ? C.white : G.shellBg,
    color: enabled ? C.teal : C.muted,
    fontSize: '0.78rem',
    fontWeight: 600,
    outline: 'none',
    cursor: enabled && canEdit ? 'text' : 'not-allowed',
  });

  const ghostBtn: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '5px',
    height: '31px',
    padding: '0 11px',
    background: G.shellBg,
    border: `1px solid ${G.line}`,
    borderRadius: '7.4px',
    color: C.teal,
    fontSize: '0.68rem',
    fontWeight: 700,
    cursor: 'pointer',
  };

  const slotNote = (key: SlotKey, v: RoleTimes) => {
    const at = to12h(v.time);
    switch (key) {
      case 'follow_up_staff':
        return v.enabled
          ? `At ${at} IST on the follow-up day, the staff member the lead is assigned to gets a "Follow-up due today" notification. Unassigned leads send nothing to staff.`
          : 'Off: assigned staff will not be reminded on the follow-up day.';
      case 'follow_up_admin':
        return v.enabled
          ? `At ${at} IST on the follow-up day, every admin gets one notification per lead, showing who it is assigned to (or "not assigned yet").`
          : 'Off: admins will not be told when a follow-up is due.';
      case 'overdue_staff':
        return v.enabled
          ? `At ${at} IST the next day, if the lead is still open and its follow-up date has not changed, the assigned staff member is told the follow-up was missed.`
          : 'Off: assigned staff will not be told about a missed follow-up.';
      case 'overdue_admin':
        return v.enabled
          ? `At ${at} IST the next day, if the lead is still open and its follow-up date has not changed, every admin is told the follow-up was missed.`
          : 'Off: admins will not be told about missed follow-ups.';
    }
  };

  const slotRow = (key: SlotKey, who: 'staff' | 'admin') => {
    if (!draft) return null;
    const v = draft[key];
    const isStaff = who === 'staff';
    return (
      <Row
        key={key}
        dim={!v.enabled}
        icon={isStaff ? <Users size={16} /> : <ShieldCheck size={16} />}
        title={isStaff ? 'Assigned staff' : 'Agency admins'}
        sub={isStaff ? 'Only for leads assigned to them' : 'For every lead, assigned or not'}
        note={slotNote(key, v)}
      >
        <input
          type="time"
          value={v.time}
          disabled={!canEdit || !v.enabled}
          onChange={(e) => onTime(key, e.target.value)}
          aria-label={`${isStaff ? 'Staff' : 'Admin'} time`}
          style={timeInput(v.enabled)}
        />
        <Toggle on={v.enabled} disabled={!canEdit} onChange={(on) => setSlot(key, { enabled: on })} label={`${isStaff ? 'Staff' : 'Admin'} notification on or off`} />
      </Row>
    );
  };

  const flagNotes: Record<FlagKey, { on: string; off: string }> = {
    notify_admin_on_qualified: {
      on: 'Admins get an instant notification when a staff member moves a lead to Qualified. Not sent when an admin does it.',
      off: 'Off: admins will not be told when staff qualify a lead.',
    },
    notify_staff_on_assigned: {
      on: 'A staff member gets one instant notification per assign action, however many leads (for example "5 new leads assigned"). Only for leads that were not already theirs.',
      off: 'Off: staff will not be told when leads are assigned to them.',
    },
  };

  const flagRow = (key: FlagKey, icon: React.ReactNode, title: string, sub: string) => {
    if (!draft) return null;
    return (
      <Row key={key} dim={!draft[key]} icon={icon} title={title} sub={sub} note={draft[key] ? flagNotes[key].on : flagNotes[key].off}>
        <Toggle on={draft[key]} disabled={!canEdit} onChange={(on) => setFlag(key, on)} label={title} />
      </Row>
    );
  };

  // ── Header ─────────────────────────────────────────────────────────────────
  const savedAt = formatSaved(updatedAt);
  const header = (
    <div style={SHELL}>
      <div style={{ ...INNER, padding: '12px 16.6px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '9px', flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: '0.6rem', fontWeight: 600, letterSpacing: '0.5px', textTransform: 'uppercase', color: C.sub }}>
            Settings · Whole agency · Times in IST
          </div>
          <h1 style={{ margin: '3.7px 0 0', fontSize: '1.01rem', fontWeight: 700, color: '#111827', letterSpacing: '-0.2px', display: 'flex', alignItems: 'center', gap: '7px' }}>
            <BellRing size={16} color={C.tealMid} /> Notification Settings
          </h1>
        </div>
        {canEdit && draft && (
          <div style={{ display: 'flex', gap: '7px', flexWrap: 'wrap' }}>
            <button type="button" onClick={resetDefaults} disabled={saving || same(draft, DEFAULTS)} style={{ ...ghostBtn, opacity: saving || same(draft, DEFAULTS) ? 0.5 : 1, cursor: saving || same(draft, DEFAULTS) ? 'not-allowed' : 'pointer' }}>
              <RotateCcw size={12} /> Reset to defaults
            </button>
          </div>
        )}
      </div>
    </div>
  );

  const banner = (tone: 'info' | 'warn', icon: React.ReactNode, text: React.ReactNode) => (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: '8px',
        padding: '9px 12px',
        borderRadius: '9.2px',
        fontSize: '0.72rem',
        lineHeight: 1.45,
        background: tone === 'warn' ? '#fffbeb' : C.limeDim,
        border: `1px solid ${tone === 'warn' ? '#fde68a' : C.limeBorder}`,
        color: tone === 'warn' ? '#92400e' : C.teal,
      }}
    >
      <span style={{ marginTop: '1px', display: 'flex' }}>{icon}</span>
      <span>{text}</span>
    </div>
  );

  // ── Loading / error ────────────────────────────────────────────────────────
  if (loading && !draft) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 0', color: C.teal }}>
        <Loader2 size={26} style={{ animation: 'ns-spin 0.9s linear infinite' }} />
        <p style={{ marginTop: '9px', fontSize: '0.81rem', fontWeight: 500 }}>Loading notification settings…</p>
        <style>{'@keyframes ns-spin{to{transform:rotate(360deg)}}'}</style>
      </div>
    );
  }

  if (error || !draft) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px', padding: '50px 0', color: '#b91c1c' }}>
        <AlertTriangle size={26} />
        <p style={{ margin: 0, fontSize: '0.81rem', fontWeight: 600 }}>{error || 'Failed to load notification settings'}</p>
        <button type="button" onClick={load} style={ghostBtn}>
          <RotateCcw size={12} /> Try again
        </button>
      </div>
    );
  }

  // ── Page ───────────────────────────────────────────────────────────────────
  return (
    <>
      <Toast ref={toast} position="top-right" />
      <style>{'@keyframes ns-spin{to{transform:rotate(360deg)}}'}</style>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '11px', paddingBottom: dirty && canEdit ? '70px' : 0 }}>
        {header}

        {!canEdit &&
          banner('warn', <Lock size={14} />, <>Only an admin can change these settings. You can see what is currently set for your agency.</>)}
        {canEdit && isDefault &&
          banner('info', <Info size={14} />, <>Your agency is using the default settings. Change anything and press <b>Save changes</b> to make them your own.</>)}
        {!isDefault && savedAt && (
          <div style={{ fontSize: '0.64rem', color: C.sub, display: 'flex', alignItems: 'center', gap: '5px' }}>
            <CheckCircle2 size={11} color={C.tealMid} /> Last saved {savedAt}
          </div>
        )}

        <Card icon={<CalendarClock size={15} />} title="Follow-up due" hint="Sent on the follow-up day set on the lead">
          {slotRow('follow_up_staff', 'staff')}
          {slotRow('follow_up_admin', 'admin')}
        </Card>

        <Card icon={<AlarmClockOff size={15} />} title="Follow-up missed" hint="Sent the day after, if the lead is still open">
          {slotRow('overdue_staff', 'staff')}
          {slotRow('overdue_admin', 'admin')}
        </Card>

        <Card icon={<Zap size={15} />} title="Instant alerts" hint="Sent right when it happens">
          {flagRow('notify_admin_on_qualified', <ShieldCheck size={16} />, 'Lead qualified', 'Staff qualifies a lead')}
          {flagRow('notify_staff_on_assigned', <Users size={16} />, 'Leads assigned', 'Admin assigns leads to staff')}
        </Card>

      <div style={SHELL}>
        <div style={{ ...INNER, padding: '12px 16px' }}>
          <div style={{ fontSize: '0.6rem', fontWeight: 600, letterSpacing: '0.5px', textTransform: 'uppercase', color: C.sub, marginBottom: '8px' }}>
            What a follow-up will do
          </div>
          {preview.length === 0 ? (
            <div style={{ fontSize: '0.74rem', color: C.muted }}>Follow-up notifications are switched off.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '7px' }}>
              {preview.map((p) => (
                <div key={`${p.kind}-${p.who}`} style={{ display: 'flex', alignItems: 'center', gap: '9px', fontSize: '0.72rem', color: '#374151' }}>
                  <span
                    style={{
                      minWidth: '92px',
                      fontSize: '0.62rem',
                      fontWeight: 700,
                      color: p.kind === 'due' ? C.teal : '#b45309',
                      background: p.kind === 'due' ? C.limeDim : '#fffbeb',
                      border: `1px solid ${p.kind === 'due' ? C.limeBorder : '#fde68a'}`,
                      borderRadius: '6px',
                      padding: '3px 7px',
                      textAlign: 'center',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {p.day === 0 ? 'Follow-up day' : 'Next day'} · {to12h(p.time)}
                  </span>
                  <span>{p.text}</span>
                </div>
              ))}
            </div>
          )}
          <div style={{ marginTop: '10px', paddingTop: '9px', borderTop: `1px solid ${G.soft}`, fontSize: '0.64rem', color: C.sub, lineHeight: 1.5 }}>
            If a follow-up is set for today after its time has passed, today&rsquo;s reminder is skipped (nobody gets a push for what was just typed); the
            missed-follow-up notice still goes out the next day. Closed, converted or re-scheduled leads never send a stale notification.
          </div>
        </div>
      </div>
      </div>

      {/* Save bar */}
      {dirty && canEdit && (
        <div style={{ position: 'sticky', bottom: 0, marginTop: '11px', zIndex: 5 }}>
          <div style={{ ...SHELL, boxShadow: '0 -6px 24px rgba(17,85,80,0.12)' }}>
            <div style={{ ...INNER, padding: '9px 12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '9px', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '0.72rem', fontWeight: 600, color: C.teal }}>You have unsaved changes</span>
              <div style={{ display: 'flex', gap: '7px' }}>
                <button type="button" onClick={discard} disabled={saving} style={{ ...ghostBtn, opacity: saving ? 0.5 : 1 }}>
                  <X size={12} /> Discard
                </button>
                <button
                  type="button"
                  onClick={save}
                  disabled={saving}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    height: '31px',
                    padding: '0 14px',
                    background: saving ? C.muted : C.teal,
                    color: C.white,
                    border: 'none',
                    borderRadius: '7.4px',
                    fontSize: '0.7rem',
                    fontWeight: 700,
                    cursor: saving ? 'not-allowed' : 'pointer',
                    boxShadow: saving ? 'none' : '0 3px 10px rgba(17,85,80,0.3)',
                  }}
                >
                  {saving ? <Loader2 size={13} style={{ animation: 'ns-spin 0.9s linear infinite' }} /> : <Save size={13} />}
                  {saving ? 'Saving…' : 'Save changes'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default NotificationSettings;